/**
 * Fetches the tenant-level preferences payload (ICP, products, competitors,
 * personas, intent topics, etc.) once per app session and re-shares it across
 * every consumer. Used by `useCopilotContext` to enrich the Copilot chat
 * context, gated by the `maCopilotContextEnrichment` LaunchDarkly flag.
 *
 * Implementation notes:
 *  - Plain `useState` + `useEffect` (no React Query in this codebase).
 *  - Module-level promise cache dedupes across consumers — exactly one
 *    network call per app session regardless of how many places call this.
 *  - The cache is cleared by `clearTenantPreferencesCache()` so the auth
 *    logout path can drop the previous user's prefs before the next login.
 */
import { useEffect, useState } from "react";

import {
  fetchTenantPreferences,
  type TenantPreferences,
} from "../apis/argo/tenantPreferences";

interface UseTenantPreferencesResult {
  data: TenantPreferences | null;
  loading: boolean;
  error: Error | undefined;
}

let cachedPromise: Promise<TenantPreferences | null> | undefined;

/**
 * Clears the in-memory cache. Call from the session-clear path so the next
 * logged-in user doesn't see the previous user's tenant prefs.
 */
export function clearTenantPreferencesCache(): void {
  cachedPromise = undefined;
}

function getOrFetch(): Promise<TenantPreferences | null> {
  if (!cachedPromise) {
    cachedPromise = fetchTenantPreferences().catch((err) => {
      // Don't poison the cache on failure — next consumer can retry.
      cachedPromise = undefined;
      throw err;
    });
  }
  return cachedPromise;
}

export function useTenantPreferences(
  enabled: boolean
): UseTenantPreferencesResult {
  const [data, setData] = useState<TenantPreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      // Disabled → don't fire, don't clobber prior data with stale state.
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    getOrFetch()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { data, loading, error };
}
